<template>
  <div class="w-full px-6 py-4">
    <div class="flex space-x-4 mb-6">
      <o-field-group
        v-slot="{ status }"
        rules="required"
        :validation-value="_product.name"
        class="w-full"
        :label="$t('general.title')"
        :info="$t('product.name')"
      >
        <text-field
          v-model="_product.name"
          :label="$t('general.title')"
          :status="status"
        />
      </o-field-group>
    </div>

    <div class="flex space-x-4 mb-6">
      <o-field-group
        class="w-full"
        :label="$t('general.information')"
        :info="$t('components.product.edit.details.label')"
      >
        <text-area-field
          v-model="_product.information"
          :label="$t('general.information')"
        />
      </o-field-group>
    </div>

    <div v-if="!_product.isNewRecord" class="flex space-x-4 mb-6">
      <o-field-group
        :label="$t('general.image')"
        :info="$t('product.visibleInLists')"
      >
        <avatar-field :model="_product" @click="handleUpdateImage" />
      </o-field-group>
    </div>

    <div class="flex space-x-4 mb-6">
      <o-field-group
        v-slot="{ status }"
        rules="required"
        :validation-value="_product.price"
        class="w-full"
        :label="$t('general.price')"
        :info="$t('components.product.edit.details.priceInfo')"
      >
        <currency-field
          v-model="_product.price"
          :label="$t('general.price')"
          :status="status"
        />
      </o-field-group>
    </div>

    <div class="flex space-x-4 mb-6">
      <o-field-group
        v-slot="{ status }"
        rules="required"
        :validation-value="_product.wholesalePrice"
        class="w-full"
        :label="$t('components.product.edit.details.wholesalePrice')"
        :info="$t('components.product.edit.details.wholesalePriceInfo')"
      >
        <currency-field
          v-model="_product.wholesalePrice"
          :label="$t('components.product.edit.details.wholesalePrice')"
          :status="status"
          :decimal-places="6"
        />
      </o-field-group>
    </div>

    <div class="flex space-x-4 mb-6">
      <o-field-group
        class="w-full"
        :label="$t('general.category')"
        :info="$t('components.product.edit.details.categoryInfo')"
      >
        <select-field
          v-model="_category"
          :options="productCategoryOptions"
          :label="$t('general.category')"
        />
      </o-field-group>
    </div>

    <div class="flex space-x-4 mb-6">
      <o-field-group
        class="w-full"
        :label="$t('general.brand')"
        :info="$t('components.product.edit.details.brandInfo')"
      >
        <select-field
          v-model="_product.brand"
          :options="brandOptions"
          :label="$t('general.brand')"
        />
      </o-field-group>
    </div>

    <div class="flex space-x-4 mb-6">
      <o-field-group
        class="w-full"
        :label="$t('components.product.edit.details.sku')"
        :info="$t('components.product.edit.details.skuInfo')"
      >
        <text-field
          v-model="_product.sku"
          :label="$t('components.product.edit.details.sku')"
        />
      </o-field-group>
    </div>

    <div class="flex space-x-4 mb-6">
      <o-field-group
        class="w-full"
        :label="$t('components.product.edit.details.barcode')"
        :info="$t('components.product.edit.details.barcodeInfo')"
      >
        <text-field
          v-model="_product.barcode"
          :label="$t('components.product.edit.details.barcode')"
        />
      </o-field-group>
    </div>

    <div class="flex space-x-4 mb-6">
      <o-field-group
        v-if="$features.get('loyalty')"
        col
        reverse
        class="w-full md:w-1/2"
        :label="$t('general.loyaltyEnabled')"
        :info="$t('components.product.edit.details.loyaltyEnabledInfo')"
      >
        <toggle-field v-model="_product.loyaltyEnabled" />
      </o-field-group>
    </div>

    <div class="my-10">
      <label class="block text-sm text-gray-700 font-medium" tabindex="0">
        <span>{{ $t("general.taxes") }}</span>
      </label>
      <p class="block text-gray-600 text-sm mb-3">
        {{ $t("components.product.edit.details.taxesInfo") }}
      </p>

      <o-field-group
        v-for="tax in taxOptions"
        :key="tax.idTax"
        v-slot="{ status }"
        :label="tax.name"
        :name="tax.name"
        :info="
          $t('tax.includeInItemCost', {
            name: tax.name,
            amount: tax.amountLabel,
          })
        "
        class="mb-4 block"
        col
        reverse
      >
        <toggle-field
          v-model="_product.taxIDs"
          :status="status"
          :value="Number(tax.idTax)"
        />
      </o-field-group>
    </div>

    <o-custom-fields :data="_product.customFields">
      <template #default="{ fields }">
        <div
          v-for="field in fields"
          :key="field.props.id"
          class="flex space-x-4 mb-4"
        >
          <o-field-group class="w-full" :label="field.label">
            <component
              :is="field.component"
              v-bind="field.props"
              v-on="field.events"
            />
          </o-field-group>
        </div>
      </template>
    </o-custom-fields>

    <update-image-dialog
      ref="updateImageDialog"
      :product="_product"
      @update="handleUpdateImageSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { OCustomFields, OAvatar } from '@/components/elements'

import UpdateImageDialog from '@/components/product/actions/Image.vue'

import {
  TextField,
  CurrencyField,
  SelectField,
  NumberField,
  TextAreaField,
  ToggleField,
  SegmentField,
  AvatarField,
} from '@/components/fields'

const { $i18n, $bus } = useNuxtApp()

interface Props {
  product: Record<string, any>
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:product', value: any): void
}>()

const updateImageDialog = ref<any>()

const _product = computed({
  get () {
    return props.product
  },
  set (value: any) {
    emit('update:product', value)
  },
})

const _category = computed({
  get () {
    return _product.value.category ?? null
  },
  set (value: any) {
    _product.value.category = value
    _product.value.idProductCategory = value?.idProductCategory || 0
  },
})

const productCategoryOptions = computed(() => {
  const productCategories = _product.value.options.productCategories

  const options = productCategories.map((productCategory: any) => {
    return {
      label: productCategory.name,
      value: productCategory,
    }
  })

  options.unshift({
    label: $i18n.t('general.none'),
    value: null,
  })

  return options
})

const brandOptions = computed(() => {
  const brands = _product.value.options.brands

  return brands.map((brand: any) => {
    return {
      label: brand.name,
      value: brand,
    }
  })
})

const taxOptions = computed(() => {
  return _product.value.options.taxes
})

const handleUpdateImage = () => {
  updateImageDialog.value.open()
}

const handleUpdateImageSuccess = (product: any) => {
  _product.value.images = product.images
  _product.value.hasImage = !!product.images
  $bus.emit.updated('Product')
}
</script>
